import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { Button, Input, TablePagination } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import createInstance from "../../../api/axiosinstance";
import Modal from "../Modal";
import Toast from "../../../utils/toast";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import { useThemeMode } from "../../Themes/ThemeContextTemp.js";

const RoleManagement = () => {
  const [roleName, setRoleName] = useState("");
  const [orgID, setOrgID] = useState("");
  const [creationDate] = useState(new Date().toISOString());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [roleData, setRoleData] = useState([]);
  const [roleId, setRoleId] = useState();
  const [editingRowId, setEditingRowId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [deactivateId, setDeactiveId] = useState("");
  const [editedId, setEditedId] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [sortingOrder, setSortingOrder] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [isActive, setIsActive] = useState("");
  const [Status, setStatus] = useState("");
  const { user } = useSelector((state) => state.auth);
  const Org_ID = user?.organizationId;
  const { COLORS } = useThemeMode();
  

  const aiDishaApi = createInstance("DISHA");

  // ✅ Regex: only alphabets, numbers and spaces
  const ROLE_NAME_REGEX = /^[A-Za-z0-9 ]*$/;

  const handleRoleNameChange = (e) => {
    const value = e.target.value;

    // Allow clearing the field
    if (value === "" || ROLE_NAME_REGEX.test(value)) {
      setRoleName(value);
    } else {
      // Optional: show toast on invalid input
      Toast.error("Role Name can contain only letters, numbers and spaces.");
    }
  };

  useEffect(() => {
    console.log("checking role mgt org_id : ", Org_ID);
    if (Org_ID) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Org_ID]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const fetchData = async () => {
    try {
      const response = await aiDishaApi.get(
        `/Org-Role-Show/all?organization_id=${Org_ID}`,
        {
          params: { Org_ID }, // pass Org_ID as query param
        }
      );

      console.log("Fetched data from API:", response.data);

      if (Array.isArray(response.data?.records)) {
        setRoleData(response.data.records);
      } else {
        console.error("Fetched data is not an array:", response.data);
        setRoleData([]); // Reset to an empty array if data is not an array
      }
    } catch (error) {
      console.error(
        "Error fetching role data:",
        error.response?.data?.message || error.message
      );
    }
  };

  const handleSort = (field) => {
    const sortedData = [...roleData].sort((a, b) => {
      const aVal = a[field] || "";
      const bVal = b[field] || "";
      if (sortingOrder === "asc") {
        return String(aVal).localeCompare(String(bVal));
      } else {
        return String(bVal).localeCompare(String(aVal));
      }
    });
    setRoleData(sortedData);
    setSortingOrder(sortingOrder === "asc" ? "desc" : "asc");
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // Filter role data based on search term
  const filteredRoleData = Array.isArray(roleData)
    ? roleData.filter((role) =>
        role.Role_Name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const handleRoleSubmit = async () => {
    try {
      const trimmedRoleName = roleName.trim();

      if (!Org_ID?.toString().trim()) {
        console.error("Organization ID cannot be empty");
        Toast.error("Organization ID cannot be empty");
        return;
      }

      if (!trimmedRoleName) {
        console.error("Role name cannot be empty");
        Toast.error("Role name cannot be empty");
        return;
      }

      if (!/^[A-Za-z0-9 ]+$/.test(trimmedRoleName)) {
        Toast.error(
          "Role Name should contain only alphabets, numbers and spaces."
        );
        return;
      }

      const existingRole = roleData.find(
        (role) => role.Role_Name === trimmedRoleName
      );
      if (existingRole) {
        console.error("Role name already exists");
        Toast.error("Role name already exists");
        return;
      }

      const response = await aiDishaApi.post("/Org-Role-Create/", {
        Org_ID: Org_ID,
        Role_Name: trimmedRoleName,
        Creation_Date: creationDate,
        status: "Active",
      });

      console.log("posted data", response);
      if (response.status === 200) {
        Toast.success("Role added successfully");
        setRoleName("");
        fetchData();
      }
    } catch (error) {
      const errorMsg =
        error?.response?.data?.detail?.[0]?.msg ||
        "Something went wrong. Please try again.";

      Toast.error(errorMsg);
      console.error("Error:", error);
    }
  };

  const handleEdit = (id, name) => {
    setRoleId(id);
    setEditedName(name);
    setRoleName(name);
    setIsEdit(true);
  };

  const handleRoleEdit = async (Role_ID) => {
    try {
      const trimmedRoleName = roleName.trim();

      if (!trimmedRoleName) {
        Toast.error("Role name cannot be empty");
        return;
      }

      if (!/^[A-Za-z0-9 ]+$/.test(trimmedRoleName)) {
        Toast.error(
          "Role Name should contain only alphabets, numbers and spaces."
        );
        return;
      }

      const existingRole = roleData.find(
        (role) => role.Role_Name === trimmedRoleName && role.Role_ID !== Role_ID
      );
      if (existingRole) {
        console.error("Role name already exists");
        Toast.error("Role name already exists");
        return;
      }

      // If the role name is unique, proceed with the update
      const updateResponse = await aiDishaApi.put(
        `/Org-Role-Edit/`,
        {}, // empty body since API expects only query params
        {
          params: {
            Org_ID: Org_ID,
            Role_ID: Role_ID,
            Role_Name: trimmedRoleName,
            updated_date: new Date().toISOString(), // if backend accepts this
          },
        }
      );

      if (updateResponse.status === 200) {
        Toast.success("Role updated successfully");
        setRoleName("");
        fetchData();
        setIsEdit(false);
      }

      const updatedRoleData = roleData.map((role) => {
        if (role.Role_ID === roleId) {
          return { ...role, Role_Name: trimmedRoleName };
        } else {
          return role;
        }
      });
      setRoleData(updatedRoleData);
      setIsEdit(false);
      setRoleName("");
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setEditingRowId(null);
    }
  };

  const handleClear = () => {
    setRoleName("");
    setOrgID("");
    setRoleId("");
    setSearchTerm("");
    setIsEdit(false);
  };

  const handleModalOpen = (Role_ID, status) => {
    setDeactiveId(Role_ID);
    setSelectedStatus(status);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleDeactivateRole = async (role_id, selected_status) => {
    const newStatus = selected_status === "Active" ? "Inactive" : "Active";
    console.log(
      "Deactivating role with ID:",
      role_id,
      "Org_ID: ",
      Org_ID,
      "to status:",
      newStatus
    );
    try {
      const response = await aiDishaApi.patch(
        `/Org-Role-status/`, // no path params
        {}, // empty body since API expects query params
        {
          params: {
            Org_ID: Org_ID,
            Role_ID: role_id,
            new_status: newStatus, // matches your API
          },
        }
      );
      if (response.status === 200) {
        Toast.success(
          newStatus === "Active"
            ? "Role activated successfully"
            : "Role deactivated successfully"
        );
        fetchData();
      }
      if (response.status === 200) {
        const updatedRoleData = roleData?.filter(
          (role) => role.Role_ID !== deactivateId
        );
        setRoleData(updatedRoleData);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const roleHeader = [
    {
      Org_ID: "Organization ID",
      Role_ID: "Role ID",
      Role_Name: "Role Name",
      Creation_Date: "Creation Date",
      status: "Status",
      Action: "Action",
    },
  ];

  const CssTextField = styled(TextField)({
    "& label": {
      color: "white",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "white",
        borderRadius: 10,
      },
      "&:hover fieldset": {
        borderColor: "white",
      },
      "&.Mui-focused fieldset": {
        borderColor: "white",
      },
      "& .MuiInputBase-input": {
        color: "white",
      },
    },
  });

  return (
    <div>
      {isEdit ? (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          sx={{
            marginTop: "30px",
            alignItems: "center",
            background: COLORS.primary,
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <Box
            gridColumn="span 6"
            sx={{ marginTop: "auto", textAlign: "left", marginBottom: "20px" }}
          >
            <Typography
              sx={{ fontFamily: "poppins", fontSize: "28px", color: COLORS.text }}
            >
              Edit Role
            </Typography>
            <Typography
              sx={{ fontFamily: "poppins", fontSize: "16px", color: COLORS.text }}
            >
              Edit the name of the Role
            </Typography>
          </Box>

          <Box
            gridColumn="span 6"
            sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
          >
            <Paper
              sx={{
                p: "2px 4px",
                display: "flex",
                width: "75%",
                borderRadius: 2,
                background: COLORS.background,
              }}
            >
              <IconButton
                type="button"
                sx={{ p: "8px", color: COLORS.text }}
                aria-label="search"
              >
                <SearchIcon />
              </IconButton>
              <InputBase
                sx={{ ml: 1, flex: 1, 
                  fontFamily: "Poppins", 
                  color: COLORS.text,
                "& input::placeholder": {
                             color: `${COLORS.text} !important`, // 👈 placeholder color
                             opacity: 10, // important (MUI reduces opacity by default)
                           }, }}
                placeholder="Search by Role Name"
                inputProps={{ "aria-label": "search roles" }}
                value={searchTerm}
                onChange={handleSearch}
              />
            </Paper>
          </Box>

          <Box
            gridColumn="span 5"
            sx={{ marginTop: "auto", textAlign: "left" }}
          >
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>
              Role Name
            </Typography>
            <TextField
              size="small"
              id="custom-css-outlined-input"
              label="Role Name"
              variant="outlined"
              value={roleName}
              onChange={handleRoleNameChange}
              sx={{
                width: "95%",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: COLORS.border,
                    borderRadius: "10px",
                  },
                  "&:hover fieldset": {
                    borderColor: COLORS.border,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: COLORS.border,
                  },
                  "& input": {
                    color: COLORS.text,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: COLORS.text,
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: COLORS.text,
                },
              }}
              required
            />
          </Box>

          <Box
            gridColumn="span 3"
            sx={{ marginTop: "auto", textAlign: "left", marginLeft: 10 }}
          >
            <Button
              onClick={handleClear}
              sx={{
                border: "1px solid",
                color:  COLORS.button,
                "&:hover": {
                  color: COLORS.hover_btn,
                },
                width: "150px",
                height: "40px",
                borderRadius: "10px",
                fontFamily: "poppins",
              }}
            >
              Clear
            </Button>
          </Box>

          <Box
            gridColumn="span 3"
            sx={{ marginTop: "auto", textAlign: "left", marginLeft: 10 }}
          >
            <Button
              sx={{
                 background:  COLORS.button,
                "&:hover": {
                  background: COLORS.hover_btn,
                },
                color: COLORS.background,
                width: "150px",
                height: "40px",
                borderRadius: "10px",
                fontFamily: "poppins",
              }}
              onClick={() => handleRoleEdit(roleId)}
            >
              <Typography sx={{ fontFamily: "poppins", color: COLORS.background }}>
                Save
              </Typography>
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          sx={{
            marginTop: "30px",
            alignItems: "center",
            background: COLORS.primary,
            borderRadius: "10px",
            padding: "15px",
          }}
        >
          <Box
            gridColumn="span 6"
            sx={{ marginTop: "auto", textAlign: "left", marginBottom: "20px" }}
          >
            <Typography
              sx={{ fontFamily: "poppins", fontSize: "28px", color: COLORS.text }}
            >
              Add Role
            </Typography>
            <Typography
              sx={{ fontFamily: "poppins", fontSize: "16px", color: COLORS.text }}
            >
              Edit the name of the Role
            </Typography>
          </Box>

          <Box
            gridColumn="span 6"
            sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
          >
            <Paper
              sx={{
                p: "2px 4px",
                display: "flex",
                width: "75%",
                borderRadius: 2,
                background: COLORS.background,
              }}
            >
              <IconButton
                type="button"
                sx={{ p: "8px", color: COLORS.text }}
                aria-label="search"
              >
                <SearchIcon />
              </IconButton>
              <InputBase
                sx={{ ml: 1, flex: 1, 
                  fontFamily: "Poppins", 
                  color: COLORS.text,
                "& input::placeholder": {
                             color: `${COLORS.text} !important`, // 👈 placeholder color
                             opacity: 10, // important (MUI reduces opacity by default)
                           },}}
                placeholder="Search by Role Name"
                inputProps={{ "aria-label": "search roles" }}
                value={searchTerm}
                onChange={handleSearch}
              />
            </Paper>
          </Box>

          <Box
            gridColumn="span 4"
            sx={{ marginTop: "auto", textAlign: "left" }}
          >
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>
              Role Name
            </Typography>
            <TextField
              size="small"
              id="outlined-input"
              label="Role Name"
              variant="outlined"
              value={roleName}
              onChange={handleRoleNameChange}
              sx={{
                width: "95%",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: COLORS.border,
                    borderRadius: "10px",
                  },
                  "&:hover fieldset": {
                    borderColor:  COLORS.border,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor:  COLORS.border,
                  },
                  "& input": {
                    color: COLORS.text,
                  },
                },
                "& .MuiInputLabel-root": {
                  color: COLORS.text,
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: COLORS.text,
                },
              }}
              required
            />
          </Box>

          <Box
            gridColumn="span 2"
            sx={{ marginTop: "auto", textAlign: "left", marginLeft: 2 }}
          >
            <Button
              onClick={handleClear}
              sx={{
                border: "1px solid",
                color:  COLORS.button,
                "&:hover": {
                  color: COLORS.hover_btn,
                },
                width: "150px",
                height: "40px",
                borderRadius: "10px",
                fontFamily: "poppins",
              }}
            >
              Clear
            </Button>
          </Box>

          <Box
            gridColumn="span 2"
            sx={{ marginTop: "auto", textAlign: "left", marginLeft: 2 }}
          >
            <Button
              sx={{
                 background:  COLORS.button,
                "&:hover": {
                  background: COLORS.hover_btn,
                },
                color: COLORS.background,
                width: "150px",
                height: "40px",
                borderRadius: "10px",
                fontFamily: "poppins",
              }}
              onClick={handleRoleSubmit}
            >
              <Typography sx={{ fontFamily: "poppins", color: COLORS.background, }}>
                Save
              </Typography>
            </Button>
          </Box>
        </Box>
      )}

      <Box xs={1} sx={{ backgroundColor: "#33333" }}>
        <Grid container spacing={1} sx={{ justifyContent: "center" }}>
          <Grid item xs={10}>
            <TableContainer
              component={Paper}
              sx={{
                marginTop: "25px",
                borderRadius: "10px",
              }}
            >
              <Table size="small">
                <TableHead sx={{ background: COLORS.primary }}>
                  {roleHeader.map(
                    ({
                      Org_ID,
                      Role_ID,
                      Role_Name,
                      Creation_Date,
                      status,
                      Action,
                    }) => (
                      <React.Fragment key={Org_ID}>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>
                          {Role_ID}
                        </TableCell>
                        <TableCell
                          sx={{
                            color:COLORS.text,
                            borderBottom: 0,
                            cursor: "pointer",
                          }}
                          onClick={() => handleSort("Role_Name")}
                        >
                          {Role_Name} {sortingOrder === "asc" ? "↑" : "↓"}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>
                          {Creation_Date}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>
                          {status}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>
                          {Action}
                        </TableCell>
                      </React.Fragment>
                    )
                  )}
                </TableHead>
                <TableBody>
                  {filteredRoleData &&
                    filteredRoleData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((n, index) => (
                        <TableRow
                          key={n.Org_ID || `${n.Role_ID}-${index}`}
                          sx={{
                            background: COLORS.background,
                            marginRight: "30px",
                          }}
                        >
                          <TableCell
                            sx={{ color: COLORS.text, border: COLORS.secondary }}
                          >
                            {n.Role_ID}
                          </TableCell>
                          <TableCell
                            sx={{ color: COLORS.text, border: COLORS.secondary }}
                          >
                            {editingRowId === n.Role_ID ? (
                              <Input
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                              />
                            ) : (
                              n.Role_Name
                            )}
                          </TableCell>
                          <TableCell
                            sx={{ color: COLORS.text, border: COLORS.secondary }}
                          >
                            {n.Creation_Date
                              ? new Date(n.Creation_Date).toLocaleDateString(
                                  "en-GB"
                                )
                              : ""}
                          </TableCell>
                          <TableCell
                            sx={{ color: COLORS.text, border: COLORS.secondary }}
                          >
                            {n.Status}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: COLORS.secondary,
                              borderRight: COLORS.secondary,
                              display: "flex",
                              alignItems: "center",
                              fontFamily: "poppins",
                            }}
                          >
                            {editingRowId === n.Role_ID ? (
                              <Button
                                variant="contained"
                                sx={{
                                  backgroundColor: "black",
                                  color: "#A7A7A7",
                                  marginRight: "10px",
                                  fontFamily: "poppins",
                                  height: "20px",
                                  width: "30px",
                                }}
                              >
                                Save
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                onClick={() =>
                                  handleEdit(n.Role_ID, n.Role_Name)
                                }
                                sx={{
                                  background: COLORS.button_edit_bg,
                                  "&:hover": {
                                    background: COLORS.hover_edit_btn,
                                  },
                                  color: COLORS.background,
                                  marginRight: "10px",
                                  fontFamily: "poppins",
                                  height: "20px",
                                  width: "30px",
                                }}
                              >
                                Edit
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              sx={{
                                backgroundColor:
                                  n.Status === "Active" ? COLORS.button_red_bg : COLORS.button_green_bg,
                                color: COLORS.text,
                                fontFamily: "poppins",
                                height: "24px",
                                padding: "2px 8px",
                                minWidth: "90px",
                                fontSize: "0.75rem",
                                "&:hover": {
                                    background:
                                      n.status === "Active"
                                        ? COLORS.hover_red_btn      // hover when Active (Deactivate button)
                                        : COLORS.hover_green_btn,   // hover when Inactive (Activate button)
                                  },
                              }}
                              onClick={() => {
                                console.log(
                                  "Deactivate button clicked for:",
                                  n.Role_ID,
                                  n.Status
                                );
                                handleModalOpen(n.Role_ID, n.Status);
                              }}
                            >
                              {n.Status === "Active"
                                ? "Deactivate"
                                : "Activate"}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 15]}
                component="div"
                count={filteredRoleData ? filteredRoleData.length : 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  background: COLORS.primary,
                  color: COLORS.text,
                }}
              />
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        title="Role"
        action={() => handleDeactivateRole(deactivateId, selectedStatus)}
        actionType={selectedStatus === "Active" ? "Deactivate" : "Activate"}
      />
    </div>
  );
};

export default RoleManagement;